import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { IPlaceResult } from './IPlaceResult';

/*#30525 : map debug. We have a less restrictive filter so we
update the max number of results to show*/
const RESULTS_NUMBER: number = 25;
const AEROWAY_FIELDS: string[] = ["aerodrome"];
const AMENITY_FIELDS: string[] = ["university", "library", "bus_station"];
const BOUNDARY_FIELDS: string[] = ["forest", "national_park", "place", "protected_area"];
const BUILDING_FIELDS: string[] = ["hotel", "cathedral", "chapel", "church", "kingdom_hall", "monastery"
    , "mosque", "religious", "synagogue", "temple", "train_station", "transportation", "university"
    , "sports_hall", "stadium", "bridge"];
const LEISURE_FIELDS: string[] = ["beach_resort", "park", "sports_center", "stadium"];
const NATURAL_FIELDS: string[] = ["beach", "glacier", "geyser", "hot_spring", "reef", "dune", "valley"];
const PLACE_FIELDS: string[] = ["country", "state", "region", "province", "county", "city", "borouhg", "suburb"
    , "quarter", "town", "village", "hamlet", "island"];
const PUBLIC_TRANSPORT_FIELDS: string[] = ["station"];
const RAILWAY_FIELDS: string[] = ["station", "subway_entrance", "tram_station"];

@Injectable()
export class NominatimResourceService {
    public constructor(
        private readonly httpClient: HttpClient
    ) { }

    public search(query: string, count: number = RESULTS_NUMBER): Observable<IPlaceResult[]> {
        return this.httpClient
            .get(` https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&addressdetails=1&format=json&limit=20`, {
                responseType: 'json',
                observe: 'response',
            })
            .pipe(
                map((value: HttpResponse<INominatimPlaceResult[]>) => {
                    if (!value.body) { return []; }
                    /*#30525 : map debug. New less restrictive filter*/
                    return value.body
                        .filter((entry: INominatimPlaceResult): boolean =>
                            (entry.class.toLowerCase() === "aeroway" && AEROWAY_FIELDS.includes(entry.type)) ||
                            (entry.class.toLowerCase() === "amenity" && AMENITY_FIELDS.includes(entry.type.toLowerCase())) ||
                            (entry.class.toLowerCase() === "boundary" && BOUNDARY_FIELDS.includes(entry.type.toLowerCase())) ||
                            (entry.class.toLowerCase() === "building" && BUILDING_FIELDS.includes(entry.type.toLowerCase())) ||
                            (entry.class.toLowerCase() === "historic") ||
                            (entry.class.toLowerCase() === "leisure" && LEISURE_FIELDS.includes(entry.type.toLowerCase())) ||
                            (entry.class.toLowerCase() === "natural" && NATURAL_FIELDS.includes(entry.type.toLowerCase())) ||
                            (entry.class.toLowerCase() === "place" && PLACE_FIELDS.includes(entry.type.toLowerCase())) ||
                            (entry.class.toLowerCase() === "public transport" && PUBLIC_TRANSPORT_FIELDS.includes(entry.type.toLowerCase())) ||
                            (entry.class.toLowerCase() === "railway" && RAILWAY_FIELDS.includes(entry.type.toLowerCase())) ||
                            (entry.class.toLowerCase() === "tourism") ||
                            (entry.type.toLowerCase() === "administrative" && Boolean(entry.address.city || entry.address.village)))
                        .map((result: INominatimPlaceResult): IPlaceResult => ({
                            id: result.place_id,
                            name: [
                                (result.class==="boundary") ? result.address.boundary : "",
                                result.address.tourism,
                                result.address.city || result.address.village,
                                result.address.state,
                                result.address.country]
                                .filter(Boolean)
                                .join(', '),
                            bounds: {
                                ne: { latitude: result.boundingbox[0], longitude: result.boundingbox[2] },
                                sw: { latitude: result.boundingbox[1], longitude: result.boundingbox[3] },
                            }
                        }))
                        .filter((entry: IPlaceResult, index: number) => index < count);
                })
            );
    }
}

interface INominatimPlaceResult {
    boundingbox: [
        number,
        number,
        number,
        number
    ];
    display_name: string;
    lat: number;
    lon: number;
    osm_id: number;
    osm_type: string;
    place_id: number;
    type: string;
    class: string;
    address: {
        boundary?: string;
        city?: string;
        village?: string;
        country: string;
        country_code: string;
        county: string;
        postcode: string;
        state: string;
        state_district: string;
        tourism?: string;
    };

}